import { Component, computed, effect, inject, OnDestroy, signal, ChangeDetectionStrategy } from '@angular/core'
import { Router, ActivatedRoute } from '@angular/router'
import { Supplier } from 'depoto-core/src/entities'
import { historyBack } from '../../utils'
import { NotificationService } from '../../services'
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms'
import { CheckDataChanges } from '../../services/can-deactivate-guard.service'
import { TranslateService } from '@ngx-translate/core'
import { TitleService } from '../../services'
import { injectMutation, injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental'
import { SupplierService } from '../../services/entity-services/supplier.services'
import { updateCachedListQueries } from '../../services/query-cache.service'

const supplierSchema = {
  id: null,
  name: null,
  externalId: null,
  company: {
    id: null,
    name: null,
  },
  // isDeletable: null todo add for disable delete option
}

@Component({
  selector: 'app-supplier',
  templateUrl: 'supplier.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SupplierComponent implements CheckDataChanges, OnDestroy {
  private supplierService = inject(SupplierService)
  private queryClient = injectQueryClient()

  // Overridden in the constructor below and restored in ngOnDestroy - see there for why.
  private previousShouldReuseRoute: Router['routeReuseStrategy']['shouldReuseRoute']

  private initialFormValue = signal<any>(null)
  submitted = signal(false)

  supplierForm = new FormGroup({
    name: new FormControl({ value: '', disabled: false }, [Validators.required]),
  })

  readonly querySupplier = injectQuery(() => ({
    queryKey: ['supplier', Number(this.route.snapshot.params['id'])],
    queryFn: async () => {
      const id = this.route.snapshot.params['id']
      if (id) {
        return await this.supplierService.getSupplier(Number(id), supplierSchema)
      } else {
        return new Supplier()
      }
    },
    onSuccess: data => {
      if (data?.id) {
        this.fillFormValue(data)
        // this.titleService.updateTitleWithObject(data['id'], data['name'], 'title.supplier-id') // todo if needed
      }
    },
  }))

  createSupplier = injectMutation(() => ({
    mutationFn: async () => {
      this.submitted.set(true)
      if (this.supplierForm.invalid) {
        throw new Error('Form is invalid')
      }
      const n = this.getDataToSave()

      return await this.supplierService.createSupplier(n, supplierSchema) // todo add schema
    },
    onSuccess: data => {
      this.submitted.set(false)
      // After successful create, snapshot current form as the new baseline to avoid false unsaved prompts
      const current = this.supplierForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.supplierForm.markAsPristine()

      this.notificationService.success(this.translateService.instant('alert.supplier.created'))

      // Update detail cache for this supplier
      this.queryClient.setQueryData(['supplier', Number(data.id)], data)

      // OPTIONS: update options/list caches so selects/filters see the new supplier immediately
      updateCachedListQueries(this.queryClient, ['suppliers', 'options'], data, {
        mode: 'insert',
        insertPosition: 'start',
      })

      // LISTING ['suppliers', sortProp, sortDir]: On CREATE → refetch listing queries so ordering/pagination is correct
      this.removeSupplierListings()

      this.router.navigate(['/supplier/', data['id']], { replaceUrl: true })
    },
  }))

  updateSupplier = injectMutation(() => ({
    mutationFn: () => {
      if (this.supplierForm.invalid) {
        throw new Error('Form is invalid')
      }
      const vatDataToSave = this.getDataToSave()
      return this.supplierService.updateSupplier(vatDataToSave, supplierSchema)
    },
    onSuccess: async data => {
      this.submitted.set(false)
      // this.core.services.storage.getSync(this.core.services.storage.keys.auth.company)
      // Snapshot current form as baseline and mark pristine so guard won't prompt right after save
      const current = this.supplierForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.supplierForm.markAsPristine()
      this.notificationService.success(this.translateService.instant('alert.supplier.updated'))
      // Update detail cache for this supplier
      this.queryClient.setQueryData(['supplier', Number(data.id)], data)
      // OPTIONS: update options cache value for this supplier
      updateCachedListQueries(this.queryClient, ['suppliers', 'options'], data, { mode: 'update' })

      // LISTING ['suppliers', sortProp, sortDir]: On CREATE → refetch listing queries so ordering/pagination is correct
      this.removeSupplierListings()

      this.router.navigate(['/supplier', data['id']])
    },
  }))

  deleteSupplier = injectMutation(() => ({
    mutationFn: async () => {
      this.submitted.set(true)
      try {
        const confirmation = confirm(
          this.translateService.instant('suplier.delete.confirmation', { name: this.supplierForm.value.name })
        )
        if (!confirmation) return

        const deletionErrors = await this.supplierService.deleteSupplier(this.querySupplier.data())
        if (deletionErrors !== null) return

        this.notificationService.success(this.translateService.instant('alert.supplier.deleted'))
        const idStr = String(this.router.url.split('/')[2])
        // OPTIONS: keep as-is → update options cache by removing the supplier
        updateCachedListQueries(this.queryClient, ['suppliers', 'options'], { id: Number(idStr) } as any, {
          mode: 'delete',
        })

        // LISTING ['suppliers', sortProp, sortDir]: On DELETE → if id is present in cached pages, remove it; otherwise do nothing
        this.removeFromListingsIfPresent(Number(idStr))
        await this.router.navigate(['/suppliers'], { replaceUrl: true })
      } finally {
        this.submitted.set(false)
      }
    },
  }))

  isCreating = computed(() => {
    return !this.route.snapshot.params['id']
  })
  loading = computed(() => {
    // While creating (no id), do not block the UI with a loading overlay
    if (this.isCreating()) return false
    // When editing existing supplier, show loading only while the query is actually in flight
    return this.querySupplier.isFetching() || this.querySupplier.isPending()
  })

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private titleService: TitleService
  ) {
    // Forces this route to reload fresh (rather than being reused) when navigating between
    // different supplier ids. This mutates the app-wide Router's routeReuseStrategy though, not
    // something scoped to this component - left in place it silently breaks route reuse (e.g.
    // wizard-style child routes elsewhere, like order creation) for the rest of the session. Put
    // the original back on the way out.
    this.previousShouldReuseRoute = this.router.routeReuseStrategy.shouldReuseRoute.bind(this.router.routeReuseStrategy)
    this.router.routeReuseStrategy.shouldReuseRoute = () => false
    // Populate the form whenever query data becomes available/changes (works for cache hits too)
    effect(() => {
      const d = this.querySupplier.data()
      const id = this.route.snapshot.params['id']
      if (id && d && this.supplierForm.pristine) {
        this.fillFormValue(d)
      }
    })
  }

  ngOnDestroy(): void {
    this.router.routeReuseStrategy.shouldReuseRoute = this.previousShouldReuseRoute
  }

  public fillFormValue(supplier) {
    const value = {
      name: supplier.name ?? '',
    }

    this.supplierForm.setValue(value)
    // Store initial values snapshot and reset form dirty state
    this.initialFormValue.set(structuredClone(value))
    this.supplierForm.markAsPristine()
  }

  hasUnsavedDataChanges(): boolean {
    const current = this.supplierForm.getRawValue() as any
    const initial = this.initialFormValue() as any

    // Fallback: before snapshot is ready
    if (!initial) {
      return this.supplierForm.dirty
    }

    const norm = (v: any) => (v === undefined || v === null ? '' : String(v))
    return norm(current?.name) !== norm(initial?.name)
  }

  getDataToSave() {
    const current = this.supplierForm.getRawValue() as any
    const patch: any = {}

    // Include id when editing
    const routeId = this.route.snapshot.params['id']
    if (routeId) patch.id = Number(routeId)

    // Always include required fields
    patch.name = current.name

    return patch
  }

  goBack(): void {
    historyBack()
  }

  get formControls(): { [key: string]: AbstractControl } {
    return this.supplierForm.controls
  }

  // ===== Helpers for cache handling of supplier listings =====
  private isSuppliersListingKey(key: unknown[]): boolean {
    if (!Array.isArray(key)) return false
    if (key.length !== 3) return false
    return key[0] === 'suppliers' && typeof key[1] === 'string' && typeof key[2] === 'string'
  }

  private listingContainsId(cached: any, id: number | string): boolean {
    const idStr = String(id)
    if (!cached || !Array.isArray(cached.pages)) return false
    for (const page of cached.pages) {
      const items = Array.isArray(page) ? page : Array.isArray(page?.items) ? page.items : undefined
      if (!Array.isArray(items)) {
        continue
      }
      if (items.some((it: any) => String(it?.id) === idStr)) {
        return true
      }
    }
    return false
  }

  private removeIdFromCachedListing(key: unknown[], id: number | string): void {
    const cached: any = this.queryClient.getQueryData(key as any)
    if (!cached || !Array.isArray(cached.pages)) return
    const idStr = String(id)
    const newPages = cached.pages.map((page: any) => {
      if (Array.isArray(page)) {
        return page.filter((it: any) => String(it?.id) !== idStr)
      }
      if (Array.isArray(page?.items)) {
        return { ...page, items: page.items.filter((it: any) => String(it?.id) !== idStr) }
      }
      return page
    })
    this.queryClient.setQueryData(key as any, { ...cached, pages: newPages })
  }

  // CREATE → refetch all cached supplier listings (['suppliers', sortProp, sortDir])
  private removeSupplierListings(): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['suppliers'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      this.queryClient.removeQueries({ queryKey: key as any, exact: true })
    }
  }

  // DELETE → if present in cached listings, remove it; otherwise do nothing
  private removeFromListingsIfPresent(id: number | string): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['suppliers'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      if (!this.isSuppliersListingKey(key as unknown[])) continue
      if (!this.listingContainsId(cached, id)) continue
      this.removeIdFromCachedListing(key as unknown[], id)
    }
  }
}
